import { graphqlClient } from "@/api/hasura/client"
import {
  GetFindTestAccountsDocument,
  GetFindTestAccountsQuery,
} from "@/api/hasura/generated/graphql"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import { queryClient } from "@/api/utils/useQuery"
import {
  AccountStateSample,
  FuelKind,
  MeterKind,
  ObservedAccountState,
  UNKNOWN,
  Unknown,
} from "@/models/admin/testAccountHealth/testAccountHealth.types"
import { MeterpointParser } from "@/models/meterpoint/meterpoint.parser"
import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
import { METER_TYPES } from "@/constants/meterpoints"

const unknownState = (): ObservedAccountState => ({
  accountStatus: UNKNOWN,
  fuel: UNKNOWN,
  meterKind: UNKNOWN,
  hasActiveAgreement: UNKNOWN,
  insideRenewalWindow: UNKNOWN,
  multiMeterpoints: UNKNOWN,
  latestReadingDate: UNKNOWN,
})

const normaliseFuel = (
  fuelType: string | null | undefined,
): FuelKind | Unknown => {
  switch ((fuelType ?? "").toUpperCase()) {
    case "ELECTRICITY":
      return "ELECTRICITY"
    case "GAS":
      return "GAS"
    case "DUAL":
    case "DUAL_FUEL":
      return "DUAL"
    default:
      return UNKNOWN
  }
}

// SMETS2 > SMETS1 > TRADITIONAL. Meters with an unrecognised type
// don't count towards the ranking.
const METER_KIND_RANK: Partial<Record<MeterKind, number>> = {
  SMETS2: 3,
  SMETS1: 2,
  TRADITIONAL: 1,
}

const meterKindForType = (meterType: string): MeterKind | null => {
  const spec = METER_TYPES.find((meter) => meter.typeCode === meterType)
  if (!spec) return null
  if (!spec.isSmartMeter) return "TRADITIONAL"
  return spec.smetsVersion === 2 ? "SMETS2" : "SMETS1"
}

const strongestMeterKind = (
  meterpoints: IMeterpoint[],
): MeterKind | Unknown => {
  const strongest = meterpoints
    .flatMap((meterpoint) => meterpoint.meters.map((meter) => meter.meterType))
    .reduce<MeterKind | null>((current, meterType) => {
      const kind = meterKindForType(meterType)
      if (!kind) return current
      if (
        !current ||
        (METER_KIND_RANK[kind] ?? 0) > (METER_KIND_RANK[current] ?? 0)
      ) {
        return kind
      }
      return current
    }, null)

  return strongest ?? UNKNOWN
}

const latestReadingDate = (meterpoints: IMeterpoint[]): string => {
  const dates = meterpoints.flatMap((meterpoint) =>
    meterpoint.readings.map((reading) => reading.date),
  )
  if (dates.length === 0) return UNKNOWN

  const latest = dates.reduce((latestSoFar, date) =>
    date.isAfter(latestSoFar) ? date : latestSoFar,
  )
  return latest.format("YYYY-MM-DD")
}

// The finder returns the FULL test-account list; callers probing many
// accounts should fetch it once via fetchFinderResult() and pass the same
// promise to each fetchAccountStateSample call (see TestAccountHealthService).
export const fetchFinderResult = (): Promise<GetFindTestAccountsQuery> =>
  graphqlClient.request(GetFindTestAccountsDocument)

const fetchAccountMeterpoints = async (
  accountNumber: string,
): Promise<IMeterpoint[]> => {
  const response = await queryClient.fetchQuery(
    BackofficeRestQueries.meterpoints.forAccount(accountNumber),
  )

  return (response ?? []).map((item) => MeterpointParser.toDomain(item))
}

export const fetchAccountStateSample = async (
  accountNumber: string,
  finderPromise: Promise<GetFindTestAccountsQuery> = fetchFinderResult(),
): Promise<AccountStateSample> => {
  const errors: string[] = []
  const state = unknownState()

  const [finder, details] = await Promise.allSettled([
    finderPromise,
    fetchAccountMeterpoints(accountNumber),
  ])

  if (finder.status === "fulfilled") {
    const row = (finder.value?.find_test_accounts ?? []).find(
      (r) => r.account_num === accountNumber,
    )
    if (row) {
      state.accountStatus = row.latest_status ?? UNKNOWN
      state.fuel = normaliseFuel(row.fuel_type)
      state.hasActiveAgreement = row.is_active_agreement ?? UNKNOWN
      state.insideRenewalWindow = row.inside_renewal_window ?? UNKNOWN
      state.multiMeterpoints = row.multi_meterpoints ?? UNKNOWN
    } else {
      errors.push(
        `hasura find_test_accounts: account ${accountNumber} not found - possibly wiped by a Junifer UAT refresh`,
      )
    }
  } else {
    errors.push(`hasura find_test_accounts: ${finder.reason}`)
  }

  if (details.status === "fulfilled") {
    try {
      state.meterKind = strongestMeterKind(details.value)
      state.latestReadingDate = latestReadingDate(details.value)
    } catch (error) {
      errors.push(`meterpoints structure processing: ${error}`)
    }
  } else {
    errors.push(`meterpoints structure: ${details.reason}`)
  }

  return { fetchedAt: new Date().toISOString(), errors, state }
}
